// Empilement : chaque section, arrivée au bas de son dernier écran (ou au haut de l'écran, si elle
// est plus courte que lui), s'arrête ; la suivante glisse par-dessus pendant qu'elle recule un
// peu, comme une carte posée sur la pile. Autour de la carte qui recule, la scène prend la couleur
// de la section qui arrive : la carte s'enfonce dans la couleur suivante. Les couleurs restent
// pures, à tout arrêt. Sous le contact, la pile ne recule plus : elle s'écarte en intercalaires
// (intercalaires.ts). Sous chaque section (sauf le héros), une bande de sa couleur laisse le temps
// d'en lire la fin avant que la suivante n'arrive : l'espace de fin (mouvement.scss).
//
// Le collage est du CSS sticky : le défilement reste celui du navigateur, sans saccade. <main>
// colle aussi, pour que le contact, hors de <main>, glisse sur la dernière section. Le recul suit
// le défilement, sans retard.
//
// Mesures : sous la classe mesure-flux, posée pendant les mesures de la navigation et de
// ScrollTrigger, le collage et le recul sont annulés (mouvement.scss) ; les positions lues sont
// celles du flux.
//
// Dessin : une carte qui change d'échelle à chaque image serait redessinée en entier à chaque
// image, et toute la pile à la fois sous le contact (la fin de page saccadait). Le temps qu'elle
// bouge, une carte porte la classe en-mouvement et garde son dessin, que le compositeur ne fait
// que mettre à l'échelle (will-change, mouvement.scss). À l'arrêt du défilement, la classe tombe :
// la carte se redessine une fois, nette, à son échelle. Au repos, rien ne reste.

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { monterIntercalaires } from './intercalaires.ts';
import { RECUL } from './reglages.ts';

gsap.registerPlugin(ScrollTrigger);

/** Pose l'empilement ; rend la fonction qui le retire. Les reculs suivent le contexte GSAP. */
export function monterEmpilement(): () => void {
  const html = document.documentElement;
  const main = document.querySelector('main');
  const sections = [...document.querySelectorAll<HTMLElement>('[data-nav]')];
  const colles = sections.filter((s) => s.parentElement === main);
  if (main) colles.push(main);

  const avant = () => {
    html.classList.add('mesure-flux');
  };
  const bouger = (el: HTMLElement) => {
    el.classList.add('en-mouvement');
  };
  const arret = () => {
    for (const el of sections) el.classList.remove('en-mouvement');
  };
  // Un rafraîchissement remet les déclencheurs à jour sans défilement : aucune carte ne bouge.
  const apres = () => {
    html.classList.remove('mesure-flux');
    arret();
  };
  ScrollTrigger.addEventListener('refreshInit', avant);
  ScrollTrigger.addEventListener('refresh', apres);
  ScrollTrigger.addEventListener('scrollEnd', arret);

  // Les déclencheurs se créent avant le collage : ils se mesurent dans le flux.
  sections.forEach((s, i) => {
    const suivante = sections[i + 1];
    // Le contact, hors de <main>, a ses intercalaires.
    if (suivante?.parentElement !== main) return;
    // La scène prend la couleur de la suivante dès que le recul commence : la carte est encore à
    // pleine taille, le changement ne se voit pas.
    const scene = () => {
      document.body.style.background = getComputedStyle(suivante).backgroundColor;
    };
    gsap.to(s, {
      scale: RECUL,
      ease: 'none',
      // Le recul se fait autour du centre de ce qu'on voit : le dernier écran, ou la section.
      transformOrigin: () =>
        `50% ${Math.max(s.offsetHeight / 2, s.offsetHeight - innerHeight / 2)}px`,
      scrollTrigger: {
        trigger: suivante,
        // La section colle quand la suivante est à un écran du haut, ou à sa propre hauteur.
        start: () => `top ${Math.min(innerHeight, s.offsetHeight)}px`,
        end: 'top top',
        scrub: true,
        invalidateOnRefresh: true,
        onEnter: scene,
        onEnterBack: scene,
        onUpdate: () => {
          bouger(s);
        },
        // Revenue au repos, la section ne garde aucune transformation en ligne.
        onLeaveBack: () => {
          gsap.set(s, { clearProps: 'transform,transformOrigin,translate,rotate,scale' });
        },
      },
    });
  });

  const contact = sections.find((s) => s.parentElement !== main && !main?.contains(s));
  const retirerIntercalaires =
    main && contact ? monterIntercalaires(main, contact, bouger) : undefined;

  // Plus haute que l'écran, une section colle par le bas ; plus courte, par le haut, pour ne
  // jamais laisser voir celle d'avant au-dessus d'elle.
  const coller = () => {
    for (const el of colles) el.style.top = `${Math.min(0, innerHeight - el.offsetHeight)}px`;
  };
  coller();
  html.classList.add('a-empile');
  addEventListener('resize', coller);
  const observateur = new ResizeObserver(coller);
  for (const el of colles) observateur.observe(el);

  return () => {
    retirerIntercalaires?.();
    removeEventListener('resize', coller);
    observateur.disconnect();
    ScrollTrigger.removeEventListener('refreshInit', avant);
    ScrollTrigger.removeEventListener('refresh', apres);
    ScrollTrigger.removeEventListener('scrollEnd', arret);
    arret();
    html.classList.remove('a-empile', 'mesure-flux');
    for (const el of colles) el.style.removeProperty('top');
    document.body.style.removeProperty('background');
  };
}
